import { getToken } from "./auth";

export interface SseHandlers {
  onToken: (text: string) => void;
  onDone: (final: { citations?: unknown[] }) => void;
  onError: (err: string) => void;
}

/**
 * POSTet an einen SSE-Endpoint und parsed event/data-Frames.
 * Returns AbortController, mit dem der Stream abgebrochen werden kann.
 */
export function postSse(
  url: string,
  body: unknown,
  handlers: SseHandlers,
): AbortController {
  const ctrl = new AbortController();
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  void (async () => {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        handlers.onError(`HTTP ${res.status}: ${txt.slice(0, 200)}`);
        return;
      }
      if (!res.body) {
        handlers.onError("No response body");
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Frames sind durch \n\n getrennt
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          dispatch(frame, handlers);
        }
      }
      if (buffer.trim()) dispatch(buffer, handlers);
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      handlers.onError((e as Error).message);
    }
  })();

  return ctrl;
}

function dispatch(frame: string, h: SseHandlers) {
  let event = "message";
  let data = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trimStart();
  }
  if (!data) return;
  try {
    const obj = JSON.parse(data);
    if (event === "token" && typeof obj.text === "string") h.onToken(obj.text);
    else if (event === "done") h.onDone(obj);
    else if (event === "error") h.onError(obj.error ?? "unknown");
  } catch {
    // ignore unparseable
  }
}
